import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getJson } from '@/lib/http';
import { downloadInstallStatusWorkbook } from '@/components/proof-of-posting/installStatusWorkbook';
import {
  INSTALL_STATUS_VIEWS,
  filterInstallStatusRows,
  formatInstallStatusCount,
  formatInstallStatusPercent,
  installOutcomeLabel,
  installStatusComparisonText,
  installStatusLabel,
  installStatusNarrative,
  installStatusViewLabel,
  summarizeInstallStatus,
  type InstallStatus,
  type InstallStatusRow,
  type InstallStatusView,
} from '@shared/install-status';

type InstallStatusResponse = {
  retailerId: number;
  retailerName: string;
  cycleId: number;
  cycleName: string | null;
  cycleStart: string | null;
  cycleEnd: string | null;
  previousCycle: {
    cycleId: number;
    cycleName: string;
    submitted: number;
    leftWithManager: number;
  } | null;
  rows: InstallStatusRow[];
};

function statusClass(status: InstallStatus): string {
  switch (status) {
    case 'installed':
      return 'bg-emerald-50';
    case 'not_installed':
      return 'bg-orange-50';
    case 'not_yet_recorded':
      return 'bg-slate-100';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function formatRecorded(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Dublin',
  }).format(date);
}

function isInstallStatusView(value: string): value is InstallStatusView {
  return (INSTALL_STATUS_VIEWS as readonly string[]).includes(value);
}

export function InstallStatusPanel({
  retailerId,
  cycleId,
  campaignId,
  storeId,
}: {
  retailerId: number;
  cycleId: number;
  campaignId: number | null;
  storeId: number | null;
}) {
  const [view, setView] = useState<InstallStatusView>('not_installed');
  const [hidePhotoRequests, setHidePhotoRequests] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const query = useQuery<InstallStatusResponse>({
    queryKey: ['/api/compliance/install-status', retailerId, cycleId],
    queryFn: () => {
      const params = new URLSearchParams({
        retailerId: String(retailerId),
        cycleId: String(cycleId),
      });
      return getJson<InstallStatusResponse>(`/api/compliance/install-status?${params.toString()}`);
    },
    enabled: Number.isFinite(retailerId) && Number.isFinite(cycleId),
  });

  const scopedRows = useMemo(() => filterInstallStatusRows(query.data?.rows ?? [], {
    campaignId,
    storeId,
    view: 'all',
    hidePhotoRequests: false,
  }), [query.data?.rows, campaignId, storeId]);

  const summary = useMemo(() => summarizeInstallStatus(scopedRows), [scopedRows]);
  const tableRows = useMemo(() => filterInstallStatusRows(query.data?.rows ?? [], {
    campaignId,
    storeId,
    view,
    hidePhotoRequests,
  }), [query.data?.rows, campaignId, storeId, view, hidePhotoRequests]);

  const filtered = campaignId != null || storeId != null;
  const comparison = !filtered && query.data?.previousCycle
    ? installStatusComparisonText(
      {
        cycleName: query.data.cycleName ?? 'This cycle',
        leftWithManager: summary.leftWithManager,
        submitted: summary.submitted,
      },
      query.data.previousCycle,
    )
    : null;

  const handleExport = async () => {
    if (!query.data) return;
    setIsExporting(true);
    try {
      await downloadInstallStatusWorkbook({
        retailerName: query.data.retailerName,
        cycleName: query.data.cycleName,
        cycleStart: query.data.cycleStart,
        cycleEnd: query.data.cycleEnd,
        rows: scopedRows,
        previousCycle: filtered ? null : query.data.previousCycle,
        filtered,
      });
    } catch (exportError) {
      console.error('Install status export failed', exportError);
      const message = exportError instanceof Error ? exportError.message : String(exportError);
      window.alert(`Failed to export Excel file:\n${message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const header = (
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle>Install status</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            One row per posting task. Left with the manager is counted here, separate from a confirmed install.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={handleExport} disabled={isExporting || scopedRows.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? 'Exporting...' : 'Export Excel'}
        </Button>
      </div>
    </CardHeader>
  );

  if (query.isLoading) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading install status...</div>
        </CardContent>
      </Card>
    );
  }

  if (query.error) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-red-600">
            {query.error instanceof Error ? query.error.message : 'Failed to load install status'}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (scopedRows.length === 0) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="text-sm text-muted-foreground">No posting tasks for the selected filters.</div>
        </CardContent>
      </Card>
    );
  }

  const tiles = [
    { label: 'Installed', value: summary.installed, share: formatInstallStatusPercent(summary.installed, summary.submitted), tone: 'bg-emerald-50' },
    { label: 'Left with manager', value: summary.leftWithManager, share: formatInstallStatusPercent(summary.leftWithManager, summary.submitted), tone: 'bg-amber-50' },
    { label: 'Removed from store', value: summary.removedFromStore, share: formatInstallStatusPercent(summary.removedFromStore, summary.submitted), tone: 'bg-orange-50' },
    { label: 'Not yet recorded', value: summary.notYetRecorded, share: formatInstallStatusPercent(summary.notYetRecorded, summary.planned), tone: 'bg-slate-50' },
  ];
  if (summary.submittedWithZeroUnits > 0) {
    tiles.splice(3, 0, {
      label: 'Submitted with zero units',
      value: summary.submittedWithZeroUnits,
      share: formatInstallStatusPercent(summary.submittedWithZeroUnits, summary.submitted),
      tone: 'bg-rose-50',
    });
  }

  return (
    <Card>
      {header}
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {tiles.map((tile) => (
            <div key={tile.label} className={`rounded-md border px-3 py-2 ${tile.tone}`}>
              <div className="text-xs text-muted-foreground">{tile.label}</div>
              <div className="text-2xl font-semibold tabular-nums">{formatInstallStatusCount(tile.value)}</div>
              <div className="text-xs text-muted-foreground">
                {tile.share ? `${tile.share} of ${tile.label === 'Not yet recorded' ? 'planned' : 'submitted'}` : ''}
              </div>
            </div>
          ))}
        </div>

        {summary.reasons.length > 0 ? (
          <div className="overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-left">
                  <th className="px-3 py-2 font-semibold">Reason</th>
                  <th className="px-3 py-2 text-right font-semibold">Left with manager</th>
                  <th className="px-3 py-2 text-right font-semibold">Removed from store</th>
                  <th className="px-3 py-2 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {summary.reasons.map((reason) => (
                  <tr key={reason.reason} className="border-t">
                    <td className="px-3 py-2">{reason.reason}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInstallStatusCount(reason.leftWithManager)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInstallStatusCount(reason.removedFromStore)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatInstallStatusCount(reason.leftWithManager + reason.removedFromStore)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <p className="text-sm text-muted-foreground">{installStatusNarrative(summary)}</p>
        {comparison ? <p className="text-sm text-muted-foreground">{comparison}</p> : null}

        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={view}
            onValueChange={(value) => {
              if (isInstallStatusView(value)) setView(value);
            }}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INSTALL_STATUS_VIEWS.map((option) => (
                <SelectItem key={option} value={option}>{installStatusViewLabel(option)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={hidePhotoRequests}
              onCheckedChange={(checked) => setHidePhotoRequests(checked === true)}
            />
            Hide photo requests in the list
          </label>
          <span className="text-sm text-muted-foreground">
            {formatInstallStatusCount(tableRows.length)} {tableRows.length === 1 ? 'row' : 'rows'}
          </span>
        </div>

        {tableRows.length === 0 ? (
          <div className="text-sm text-muted-foreground">No rows for this list.</div>
        ) : (
          <div className="max-h-[70vh] overflow-auto rounded-md border">
            <table className="w-full min-w-[1100px] border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-slate-100">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">Store</th>
                  <th className="px-3 py-2 font-semibold">Campaign</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Outcome</th>
                  <th className="px-3 py-2 font-semibold">Reason</th>
                  <th className="px-3 py-2 font-semibold">Note</th>
                  <th className="px-3 py-2 font-semibold">Operator</th>
                  <th className="px-3 py-2 text-right font-semibold">Planned</th>
                  <th className="px-3 py-2 text-right font-semibold">Posted</th>
                  <th className="px-3 py-2 font-semibold">Recorded</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.taskId} className="border-t align-top">
                    <td className="px-3 py-2">
                      <div>{row.storeName}</div>
                      {row.photoRequest ? <div className="text-xs text-muted-foreground">Photo request</div> : null}
                    </td>
                    <td className="px-3 py-2">{row.campaignName}</td>
                    <td className={`px-3 py-2 ${statusClass(row.status)}`}>{installStatusLabel(row.status)}</td>
                    <td className="px-3 py-2">{installOutcomeLabel(row.outcome)}</td>
                    <td className="px-3 py-2">{row.reason ?? ''}</td>
                    <td className="max-w-xs px-3 py-2 whitespace-pre-wrap">{row.note ?? ''}</td>
                    <td className="px-3 py-2">{row.operatorName ?? ''}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInstallStatusCount(row.plannedQty)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.postedUnits == null ? '' : formatInstallStatusCount(row.postedUnits)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatRecorded(row.recordedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
